/**
 * Signup-related models and types.
 * Last modified: 2026-08-22
 *
 * These complement the RegistrationData / AuthResponse interfaces
 * already defined in auth.service.ts.  They capture domain concepts
 * specific to the trial-signup flow (language options, org-domain
 * matching, password-strength metadata, etc.).
 */

// ---------------------------------------------------------------------------
// Signup wizard steps
// ---------------------------------------------------------------------------

/** Numeric identifiers for each step of the signup wizard. */
export enum SignupStep {
  Account = 0,
  Profile = 1,
  Security = 2,
}

/** Metadata for each wizard step (used by the progress indicator). */
export interface StepInfo {
  step: SignupStep;
  label: string;
  icon: string;
}

/** Ordered step definitions consumed by the template. */
export const SIGNUP_STEPS: StepInfo[] = [
  { step: SignupStep.Account, label: 'Account',  icon: 'mail_outline' },
  { step: SignupStep.Profile, label: 'Profile',  icon: 'person_outline' },
  { step: SignupStep.Security, label: 'Security', icon: 'lock_outline' },
];

/** Steps for the Microsoft SSO signup flow (no Security step). */
export const SIGNUP_STEPS_MICROSOFT: StepInfo[] = [
  { step: SignupStep.Account, label: 'Account',  icon: 'mail_outline' },
  { step: SignupStep.Profile, label: 'Profile',  icon: 'person_outline' },
];

/** Steps for the Google signup flow (credential selection, then profile). */
export const SIGNUP_STEPS_GOOGLE: StepInfo[] = [
  { step: SignupStep.Account, label: 'Account',  icon: 'mail_outline' },
  { step: SignupStep.Profile, label: 'Profile',  icon: 'person_outline' },
];

// ---------------------------------------------------------------------------
// Trial constants
// ---------------------------------------------------------------------------

/** Number of days in the free trial period. */
export const TRIAL_DAYS = 7;

/** Feature highlights displayed on the signup marketing panel. */
export interface TrialFeature {
  icon: string;
  title: string;
  description: string;
}

export const TRIAL_FEATURES: TrialFeature[] = [
  {
    icon: 'terminal',
    title: 'Nova CLI',
    description: 'The most powerful agentic AI on your computer — real-time code assistance, debugging and generation.',
  },
  {
    icon: 'hub',
    title: 'Knowledge Base',
    description: 'Centralise your organisation\'s knowledge in one place — empower every team member to instantly search, discover and leverage shared expertise.',
  },
  {
    icon: 'table_chart',
    title: 'Gilford, Barnaby & Marlowe',
    description: 'Agents for Excel, PowerPoint and Word — build spreadsheets, presentations and documents with natural language.',
  },
  {
    icon: 'desktop_mac',
    title: 'Compass Partner',
    description: 'Cross-platform desktop app that connects to your organisation\'s knowledge and takes action.',
  },
  {
    icon: 'cloud_sync',
    title: 'Microsoft 365 Integration',
    description: 'OneDrive, SharePoint, Teams and Outlook — seamlessly connected through secure Graph API.',
  },
];

// ---------------------------------------------------------------------------
// Language
// ---------------------------------------------------------------------------

/** A selectable language option shown in the signup form. */
export interface LanguageOption {
  /** ISO 639-1 code sent to the backend (e.g. 'en', 'pt', 'es'). */
  code: string;
  /** Human-readable label displayed in the dropdown. */
  label: string;
}

/** Pre-defined language choices available at signup. */
export const SUPPORTED_LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English' },
  { code: 'pt', label: 'Português' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
];

/** The default language code used when none is explicitly selected. */
export const DEFAULT_LANGUAGE = 'en';

// ---------------------------------------------------------------------------
// Company name validation
// ---------------------------------------------------------------------------

/** Maximum accepted length for a submitted company name. */
export const COMPANY_NAME_MAX_LENGTH = 100;

/** Placeholder values that are not genuine company names. */
export const INVALID_COMPANY_NAME_PLACEHOLDERS: ReadonlySet<string> = new Set([
  '?',
  '-',
  'n/a',
  'na',
  'none',
  'unknown',
  'test',
  'test company',
  'company',
  'my company',
  'abc',
  'asdf',
  'qwerty',
  'xxx',
]);

/** Normalizes user-entered company names before validation/submission. */
export function normalizeCompanyName(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

/** Returns `true` when a company name is too generic, malformed, or junk. */
export function isInvalidCompanyName(value: string): boolean {
  const normalized = normalizeCompanyName(value);
  if (!normalized) return false;

  const lower = normalized.toLowerCase();
  const compact = lower.replace(/[\s._-]+/g, '');
  const digitCount = (normalized.match(/\d/g) ?? []).length;

  return (
    normalized.length < 2 ||
    normalized.length > COMPANY_NAME_MAX_LENGTH ||
    INVALID_COMPANY_NAME_PLACEHOLDERS.has(lower) ||
    !/\p{L}/u.test(normalized) ||
    /^[^\p{L}\p{N}]+$/u.test(normalized) ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized) ||
    /^(https?:\/\/|www\.)/i.test(normalized) ||
    (/^[+\d\s().-]+$/.test(normalized) && digitCount >= 7) ||
    (compact.length >= 4 && /^(.)\1+$/.test(compact))
  );
}

// ---------------------------------------------------------------------------
// Organisation domain matching
// ---------------------------------------------------------------------------

/**
 * Represents an existing organisation that matches the email domain
 * entered during signup.  Returned by the backend's domain-lookup
 * endpoint so the frontend can show "We found your organisation".
 */
export interface OrgDomainMatch {
  org_id: number;
  org_name: string;
  domain: string;
}

/**
 * Response from the domain-lookup endpoint.
 * When `taken` is true, the domain belongs to an existing organisation
 * and the user should be blocked from proceeding with signup.
 */
export interface DomainLookupResponse {
  /** Whether the email domain is already taken by an existing organisation. */
  taken: boolean;
  /** Name of the organisation that owns the domain (present when taken is true). */
  org_name?: string;
}

// ---------------------------------------------------------------------------
// Signup response
// ---------------------------------------------------------------------------

/**
 * Extra fields the backend may include in the signup response on top
 * of the standard BaseResponseModel ({ success, message }).
 */
export interface SignupResult {
  /** Whether the API call succeeded. */
  success: boolean;
  /** Human-readable message about the org the user was assigned to. */
  message?: string;
}

// ---------------------------------------------------------------------------
// Password strength
// ---------------------------------------------------------------------------

/** Individual criteria checked by the password-strength validator. */
export interface PasswordCriteria {
  label: string;
  met: boolean;
}

/** Labels for the password criteria shown in the strength indicator. */
export const PASSWORD_CRITERIA_LABELS: readonly string[] = [
  'At least 8 characters',
  'One uppercase letter',
  'One lowercase letter',
  'One number',
  'One special character (!@#$...)',
];

/** Regex patterns used by the password-strength validator. */
export const PASSWORD_PATTERNS = {
  uppercase: /[A-Z]/,
  lowercase: /[a-z]/,
  numeric: /[0-9]/,
  special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/,
} as const;

// ---------------------------------------------------------------------------
// Personal email domain filter
// ---------------------------------------------------------------------------

/**
 * Common personal-email domains that should be excluded from the
 * organisation domain-lookup (no point matching "gmail.com" to an org).
 */
export const PERSONAL_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  // Google
  'gmail.com',
  'googlemail.com',

  // Microsoft / Outlook / Hotmail
  'outlook.com',
  'outlook.co.uk',
  'outlook.fr',
  'outlook.de',
  'outlook.es',
  'outlook.it',
  'outlook.pt',
  'hotmail.com',
  'hotmail.co.uk',
  'hotmail.fr',
  'hotmail.de',
  'hotmail.es',
  'hotmail.it',
  'live.com',
  'live.co.uk',
  'live.fr',
  'live.de',
  'live.nl',
  'msn.com',

  // Yahoo
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.fr',
  'yahoo.de',
  'yahoo.es',
  'yahoo.it',
  'yahoo.co.jp',
  'yahoo.com.br',
  'yahoo.co.in',
  'ymail.com',
  'rocketmail.com',

  // Apple
  'icloud.com',
  'me.com',
  'mac.com',

  // AOL / Verizon
  'aol.com',
  'aim.com',

  // Privacy-focused free providers
  'protonmail.com',
  'protonmail.ch',
  'proton.me',
  'pm.me',
  'tutanota.com',
  'tutanota.de',
  'tuta.io',
  'tutamail.com',
  'mailfence.com',
  'hushmail.com',
  'hushmail.me',

  // Zoho free tier
  'zoho.com',
  'zohomail.com',

  // GMX / Mail.com / Web.de
  'gmx.com',
  'gmx.net',
  'gmx.de',
  'gmx.at',
  'gmx.ch',
  'mail.com',
  'email.com',
  'web.de',

  // Yandex
  'yandex.com',
  'yandex.ru',
  'ya.ru',

  // Mail.ru
  'mail.ru',
  'inbox.ru',
  'bk.ru',
  'list.ru',

  // Regional European providers
  'free.fr',
  'laposte.net',
  'orange.fr',
  'wanadoo.fr',
  'sfr.fr',
  'libero.it',
  'virgilio.it',
  'tin.it',
  'wp.pl',
  'o2.pl',
  'interia.pl',
  'seznam.cz',
  'centrum.cz',
  'sapo.pt',
  'iol.pt',
  'terra.com.br',
  'uol.com.br',
  't-online.de',
  'bluewin.ch',

  // Other popular free providers
  'fastmail.com',
  'runbox.com',
  'startmail.com',
  'posteo.de',
  'posteo.net',
  'disroot.org',
  'riseup.net',
  'cock.li',
]);

/** Temporary/disposable email domains that are never eligible for signup. */
export const DISPOSABLE_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'guerrillamail.com',
  'guerrillamail.de',
  'tempmail.com',
  'throwaway.email',
  'mailinator.com',
  'yopmail.com',
  'sharklasers.com',
  'guerrillamailblock.com',
  'grr.la',
  'dispostable.com',
  'temp-mail.org',
  'fakeinbox.com',
  'trashmail.com',
  'trashmail.me',
  'mohmal.com',
  'maildrop.cc',
  'tm.515666.xyz',
  'bltiwd.com',
  'mail.g-9.vactor1.us.ci',
  'inboxkitten.com',
  'ddker.com',
  'throwawaymail.app',
  'uncorreotemporal.com',
  'forliion.com',
  'tempmail.cn',
  'disbox.net',
  'mailistry.com',
  'moakt.cc',
  'linshi.co',
  'mjj.cm',
  'xghff.com',
  'catchmail.io',
  '2048unblocked.com',
  'tempmail.io',
  'mailforspam.com',
  'gmeenramy.com',
  'fake.legal',
  'bwmyga.com',
  '10mail.xyz',
  'zeppost.com',
  'cool3r.net',
  'harakirimail.com',
  'blondmail.com',
  'smail.pw',
  'mail123.fr',
  '6.airfryersbg.com',
  'disposable.email',
  'ozsaip.com',
  'aatrox.org',
  'mailto.plus',
  'ruutukf.com',
  'youxiang.dev',
  'kortazo.email',
  'p5.airfryersbg.com',
  'dbwot.com',
  '6k3so4.cn.mt',
  'iopia.org',
  'getnada.net',
  'inboxly.website',
  'xlv.gooncraft.de',
  '0m0.abrdns.com',
  'ambes.org',
  'b7o.gooncraft.de',
  '4h.actionvspot.com',
  'trashmails.lol',
  'web-library.net',
  'u2.airfryersbg.com',
  'aniimate.net',
  'lnovic.com',
  'downloadvideoig.app',
  '1j.airfryersbg.com',
  'imgui.de',
  'wanting2work.org.uk',
  'ygwpr.com',
  'mailtowin.com',
  'kvarra.org',
  'linshiyou.com',
  'drmail.in',
  'workspace-googlemail.com',
  'uddinfoundation.org',
  'vi.airfryersbg.com',
  'freeml.net',
  'spymail.one',
  'emlhub.com',
  'emlpro.com',
  'm5.actionvspot.com',
  'videoigdownload.com',
  'k0jbs5hm.ccwu.cc',
  'imxwe.com',
  '206969.xyz',
  'phg.icodetensor.com',
  'mrizkifreelace.dpdns.org',
  'pl.gardianwaves.org',
  'mimimail.me',
  'emltmp.com',
  'oqqaj.com',
  'lyux39qw.cn',
  'matprivate.tokenized.name',
  'xddroot.eu.org',
  '3d.airfryersbg.com',
]);

/** Known disposable provider base domains where rotating subdomains must be blocked. */
export const DISPOSABLE_EMAIL_DOMAIN_SUFFIXES: ReadonlySet<string> = new Set([
  '515666.xyz',
]);

/**
 * Returns `true` when the given domain belongs to a well-known free
 * email provider and should therefore skip org-domain lookup.
 */
export function isPersonalEmailDomain(domain: string): boolean {
  return PERSONAL_EMAIL_DOMAINS.has(domain.toLowerCase());
}

/** Returns `true` when the given domain belongs to a disposable email service. */
export function isDisposableEmailDomain(domain: string): boolean {
  const normalized = domain.toLowerCase();
  return (
    DISPOSABLE_EMAIL_DOMAINS.has(normalized) ||
    Array.from(DISPOSABLE_EMAIL_DOMAIN_SUFFIXES).some(suffix =>
      normalized === suffix || normalized.endsWith(`.${suffix}`)
    )
  );
}

/** Returns `true` when the given domain is not eligible for signup. */
export function isBlockedSignupEmailDomain(domain: string): boolean {
  return isPersonalEmailDomain(domain) || isDisposableEmailDomain(domain);
}

// ---------------------------------------------------------------------------
// Microsoft signup payload
// ---------------------------------------------------------------------------

/** Data sent to POST /auth/microsoft-signup. */
export interface MicrosoftSignupData {
  id_token: string;
  full_name: string;
  company_name: string;
  text: string;
  language: string;
}

// ---------------------------------------------------------------------------
// Google authentication
// ---------------------------------------------------------------------------

/** One-time nonce issued by the backend before each Google credential request. */
export interface GoogleNonceResponse {
  nonce: string;
  expires_in: number;
}

/** Data sent to POST /auth/google-signup. */
export interface GoogleSignupData {
  id_token: string;
  nonce: string;
  full_name: string;
  company_name: string;
  text: string;
  language: string;
}

/** Non-authenticating guidance returned with HTTP 200 by Google signin. */
export interface GoogleSignInGuidance {
  success: false;
  reason: 'no_org' | 'no_user' | 'inactive';
  detail: string;
}

/** Normalized Google authentication error exposed to the UI. */
export interface GoogleAuthError {
  message: string;
  status: number;
  reason?: string;
  detail?: string;
}

// ---------------------------------------------------------------------------
// Form value shape
// ---------------------------------------------------------------------------

/**
 * Type-safe shape of the signup reactive form's `.value`.
 * Keeps the template and component in sync with the backend contract.
 */
export interface SignupFormValue {
  email: string;
  full_name: string;
  company_name: string;
  text: string;
  language: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}
